<template>

    <ui-panel
        class="section"
        :class="{ collapsable: collapsible, open }"
        :padding="false"
        v-spinner="loading">

        <div class="section-title" @click.stop="$emit('toggle')">
            <div class="left">
                <div class="collapse-indicator"><i class="magicon-arrow-down-1"></i></div>
                <h2>Vak {{ section.name }}</h2>
                <span class="efficiency">(Rendement: {{ section.efficiency * 100 | number }}%)</span>
                <span class="efficiency" v-if="totalSowingDensity != null">(Zaaidichtheid: {{ totalSowingDensity | number }} mTon/ha)</span>
            </div>

            <div class="right">

                <div class="action-filter" v-if="actionOptions.length > 1">
                    <ui-dropdown v-model="selectedActionType" :options="actionOptions" empty="Alle acties"/>
                </div>

                <div class="date-navigator">
                    <ui-button
                        color="light"
                        icon="magicon-arrow-left-1"
                        class="prev"
                        :disabled="!canNavigateDate(-1)"
                        @click="navigateDate(-1)" />
                    <span class="title">{{ dateTitle }}</span>
                    <ui-button
                        color="light"
                        icon="magicon-arrow-right-1"
                        class="next"
                        :disabled="!canNavigateDate(1)"
                        @click="navigateDate(1)" />
                </div>
                <template v-if="editable && cohort">
                    <ui-button
                        v-if="!cohort.endDate"
                        color="negative"
                        class="button-right"
                        @click="closeCohort()">Cohort afsluiten</ui-button>
                    <ui-button v-else disabled class="button-right">Cohort afgesloten</ui-button>
                </template>
            </div>

        </div>

        <ui-table class="action-table" :placeholder="section && !actions.length" clickable>
            <ui-table-header>
                <ui-th weight="1.5">Datum</ui-th>
                <ui-th weight="2">Actie</ui-th>
                <ui-th weight="1">Visgebied</ui-th>
                <ui-th weight="1">Bruto</ui-th>
                <ui-th weight="1">Netto</ui-th>
                <ui-th weight="1">Gewicht</ui-th>
                <ui-th weight="1">Tarra</ui-th>
                <ui-th weight="1">Bustal</ui-th>
                <ui-th weight="1">Stukstal</ui-th>
                <ui-th weight="1">Zaaidichtheid</ui-th>
                <ui-th weight="1">Visuren</ui-th>
                <ui-th weight="1">Foto's</ui-th>
                <ui-th weight="1"/>
            </ui-table-header>
            <ui-tr v-for="action in actions" :key="`${action.__typename}_${action.id}`" click="detail">
                <td>{{ action.date | date("DD-MM-YYYY") }}</td>
                <td>{{ action.__typename | actionTypeTitle(action) }}</td>
                <td><template v-if="action.fishingArea">{{ action.fishingArea }}</template><template v-else>-</template></td>
                <td><template v-if="action.brutoWeight">{{ action.brutoWeight | number }} mTon</template><template v-else>-</template></td>
                <td><template v-if="action.nettoWeight">{{ action.nettoWeight | number }} mTon</template><template v-else>-</template></td>
                <td><template v-if="action.brutoWeight">{{ action.brutoWeight * 100 | number }} kg</template><template v-else>-</template></td>
                <td><template v-if="action.tarra">{{ action.tarra | number }}%</template><template v-else>-</template></td>
                <td>{{ action.bustal | number | empty }}</td>
                <td>{{ action.stukstal | number | empty }}</td>
                <td>
                    <template v-if="action.brutoWeight || action.nettoWeight">
                        {{ (action.brutoWeight || action.nettoWeight) / section.size | number }} mTon/ha
                    </template>
                    <template v-else>-</template>
                </td>
                <td>
                    <template v-if="action.fishingDuration">
                        {{ action.fishingDuration.duration | number | empty }} min
                    </template>
                    <template v-else>-</template>
                </td>
                <td>{{ action.actionImages.items.length }}</td>
                <td class="actions" @click.stop>
                    <ui-link
                        icon="entypo-eye"
                        v-tooltip="'Details'"
                        @click="openActionDetailModal(action)"
                        action="detail" />
                    <ui-link
                        v-if="editable && getEditRoute(action)"
                        icon="magicon-edit"
                        v-tooltip="'Wijzigen'"
                        :to="getEditRoute(action)" />
                    <ui-link
                        v-if="deletable"
                        icon="magicon-trash"
                        v-tooltip="'Verwijderen'"
                        @click="deleteAction(action)" />
                </td>
            </ui-tr>
        </ui-table>

        <section class="photos" v-if="actionsWithImages.length > 0">
            <h2 class="title">Foto's</h2>

            <div class="actions-container" v-for="n in actionsWithImages">
                <h3 class="date">{{ n.date | date("DD MMMM YYYY") }} – {{ n.__typename | actionTypeTitle(n) }}</h3>
                <div class="image-container">
                    <img
                        class="image"
                        :src="image.url"
                        v-for="(image, index) in n.actionImages.items"
                        @click="openPhotoViewer(n, image, index)" />
                </div>
            </div>
        </section>
    </ui-panel>

</template>

<script>

import Vue from 'vue';
import { last } from 'es-toolkit';
import Component from 'vue-class-component';
import { findIndex, reverse, sortBy } from 'es-toolkit/compat';

import dateFilter from 'js-core/js/filters/date';
import createQuery from 'js-core/js/apollo/queries/createQuery';

import ActionType from 'common/enums/ActionType';
import actionTypeTitle from 'common/filters/actionTypeTitle';

import FindCohortQuery from 'gql/queries/SectionDetail/FindCohort.gql';

import CohortMutations from 'app/mutations/CohortMutations';
import PhotoViewerPanel from 'app/components/panels/PhotoViewerPanel.vue';
import ActionDetailModal from 'app/components/modals/ActionDetailModal.vue';
import ConfirmDeleteActionModal from 'app/components/modals/ConfirmDeleteActionModal.vue';

import SampleMutations from '../../mutations/SampleMutations';
import SowingMutations from '../../mutations/SowingMutations';
import HarvestMutations from '../../mutations/HarvestMutations';
import TreatmentMutations from '../../mutations/TreatmentMutations';

@Component({

    name: 'SectionDetail',
    props: {
        parcel: Object,
        section: Object,
        collapsible: Boolean,
        editable: Boolean,
        deletable: Boolean,
        open: Boolean,
        type: String,
        startDate: String,
        endDate: String
    },
    apollo: {

        cohort: createQuery(FindCohortQuery, {

            skip(){

                return !this.cohortId;
            },

            variables(){

                return {
                    id: this.cohortId,
                    type: this.type,
                    startDate: this.startDate,
                    endDate: this.endDate
                };
            }
        })
    }
})
export default class SectionDetail extends Vue {

    loading = 0;

    cohortId = null;

    selectedActionType = null;

    created(){

        this.$watch('section', () => this.updateCohortId());

        this.$watch('cohortId', () => this.selectedActionType = null);

        this.updateCohortId();
    }

    updateCohortId(){

        if(!this.section || this.cohortId){
            return;
        }

        const lastCohort = last(this.cohorts);
        this.cohortId = lastCohort ? lastCohort.id : null;
    }

    openPhotoViewer(action, image, index){
        this.$modal(PhotoViewerPanel, {
            image,
            section: this.section,
            action,
            index
        });
    }

    async openActionDetailModal(action) {

        const cohort = this.cohort;
        let treatment = null;

        if(action.__typename === ActionType.SOWING){
            treatment = cohort.actions.find(actionItem => actionItem.__typename === ActionType.TREATMENT && actionItem.sowingId === action.id) || null;
        }
        else if(action.__typename === ActionType.HARVEST){
            treatment = cohort.actions.find(actionItem => actionItem.__typename === ActionType.TREATMENT && actionItem.harvestId === action.id) || null;
        }

        this.$modal(ActionDetailModal, {
            action,
            section: this.section,
            treatment,
            parcel: this.parcel
        });
    }

    navigateDate(direction){

        if(!this.cohort || !this.canNavigateDate(direction)){
            return;
        }

        const cohortIndex = findIndex(this.cohorts, { id: this.cohort.id });
        this.cohortId = this.cohorts[cohortIndex + direction].id;
    }

    canNavigateDate(direction){

        if(!this.cohort){
            return false;
        }

        const cohortIndex = findIndex(this.cohorts, { id: this.cohort.id });

        if(direction < 0){
            return cohortIndex > 0;
        }
        else if(direction > 0){
            return cohortIndex < this.cohorts.length - 1;
        }
    }

    getEditRoute(action){

        const type = action.__typename;
        let routeName = null;

        if(type === ActionType.TREATMENT){
            routeName = 'treatment-edit';
        }
        else if(type === ActionType.SAMPLE){
            routeName = 'sample-edit';
        }
        else if(type === ActionType.HARVEST){
            routeName = 'harvest-edit';
        }
        else if(type === ActionType.SOWING){
            routeName = 'sowing-edit';
        }

        return routeName ? {
            name: routeName,
            params: { id: action.id }
        } : null;
    }

    async deleteAction(action){

        const { result } = await this.$modal(ConfirmDeleteActionModal, {
            action
        });

        if(!result){
            return;
        }

        const type = action.__typename;

        try {

            if(type === ActionType.TREATMENT){
                await TreatmentMutations.delete(action.id);
            } else if(type === ActionType.SAMPLE){
                await SampleMutations.delete(action.id);
            } else if(type === ActionType.HARVEST){
                await HarvestMutations.delete(action.id);
            } else if(type === ActionType.SOWING){
                await SowingMutations.delete(action.id);
            } 

            this.$toast.success('De actie(s) is/zijn verwijderd');
            this.$emit('delete', action);

            await this.$refetchQuery('cohort');
        }
        catch(e){

            this.logger.error(e);
            this.$toast.error('De actie(s) kon(den) niet worden verwijderd');
        }
    }

    async closeCohort(){

        const cohort = this.cohort;
        const { result, overlay } = await this.$alert.confirm('Let op! Het huidige cohort wordt afgesloten en er wordt een nieuwe aangemaakt. Alle percelen op dit cohort en bijbehorende statistieken worden gereset.', true);
        if(!result){
            return;
        }

        try {

            await CohortMutations.closeCohort(cohort.id);
            this.$toast.success('Het cohort is afgesloten');

            this.$emit('cohort-close', cohort);
        }
        catch(e){

            this.logger.error(e);
            this.$toast.error('Het cohort kon niet worden afgesloten');
        }

        this.$overlay.close(overlay);
    }

    get actionOptions() {

        if (!this.cohort || !this.cohort.actions?.length) {
            return [];
        }

        const actionTypeMap = this.cohort.actions.reduce((acc, action) => {
            if (!acc[action.__typename]) {
                acc[action.__typename] = action;
            }
            return acc;
        }, {});

        return Object.entries(actionTypeMap).map(([typeName, sampleAction]) => ({
            label: actionTypeTitle(typeName, sampleAction),
            value: typeName
        }));
    }

    get dateTitle(){

        if(this.loading){
            return null;
        }

        if(!this.cohort){
            return 'Geen cohorts';
        }

        const parts = [dateFilter(this.cohort.startDate, 'D MMM YYYY')];

        if(this.cohort.endDate){
            parts.push(dateFilter(this.cohort.endDate, 'D MMM YYYY'));
        }
        else {
            parts.push('heden');
        }

        return parts.join(' - ');
    }

    get cohorts(){

        if(!this.section){
            return [];
        }

        return sortBy(this.section.cohorts.items, 'startDate');
    }

    get actions(){

        if(!this.cohort){
            return [];
        }

        return reverse(sortBy(this.cohort.actions.filter(action =>
            !(action.__typename === ActionType.TREATMENT && action.sowingId) &&
                (this.selectedActionType === null || action.__typename === this.selectedActionType)
        ), 'date'));
    }

    get actionsWithImages(){

        return this.actions.filter(action => action.actionImages.items.length);
    }

    get totalSowingDensity() {

        if(!this.section?.size || !this.cohort) {
            return null;
        }

        let total = 0;
        let hasValue = false;

        for(const action of this.actions) {

            if(action.__typename !== ActionType.SOWING) {
                continue;
            }

            const weight = action.brutoWeight || action.nettoWeight;

            if(!weight) {
                continue;
            }

            total += weight / this.section.size;
            hasValue = true;
        }

        return hasValue ? total : null;
    }
}

</script>